import React, { useState } from "react";
import styles from "@/styles/components/AddCampaignDialog.module.css";
import { useData } from "@/contexts/DataContext";
import { useTranslation } from "react-i18next";

const AddCampaignDialog = () => {
  const { t } = useTranslation();
  const { createCampaign } = useData();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Normalize endsAt to end-of-day (23:59:59.999) if user provided only a date.
      function normalizeEnd(dateStr){
        if(!dateStr) return undefined;
        // Already has time portion? we leave as-is.
        if(/T\d{2}:\d{2}/.test(dateStr)) return new Date(dateStr).toISOString();
        const d = new Date(dateStr + 'T23:59:59.999');
        return d.toISOString();
      }
      function normalizeStart(dateStr){
        if(!dateStr) return undefined;
        if(/T\d{2}:\d{2}/.test(dateStr)) return new Date(dateStr).toISOString();
        const d = new Date(dateStr + 'T00:00:00.000');
        return d.toISOString();
      }
      await createCampaign({
        name,
        description: description || undefined,
        budget: budget ? parseFloat(budget) : undefined,
        startsAt: normalizeStart(startsAt),
        endsAt: normalizeEnd(endsAt),
      });
      setName("");
      setDescription("");
      setBudget("");
      setStartsAt("");
      setEndsAt("");
      setOpen(false);
    } catch (err) {
      setError(err.message || t('common.error') || 'Error');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="bg-blue-600 text-white px-4 py-2 rounded text-sm"
      >
        {t('merchant.campaigns.newCampaign')}
      </button>
    );
  }

  return (
    <div className={styles.backdrop}>
      <form
        onSubmit={submit}
        className="bg-background rounded shadow p-6 w-full max-w-md space-y-4"
      >
        <h2 className="text-lg font-semibold">{t('merchant.campaigns.create')}</h2>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('common.name')} <span className="text-red-600">*</span></label>
          <input
            className="border px-3 py-2 rounded w-full text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('common.description')}</label>
          <textarea
            className="border px-3 py-2 rounded w-full text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Budget (€)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="border px-3 py-2 rounded w-full text-sm"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Start <span className="text-red-600">*</span></label>
            <input
              type="date"
              className="border px-3 py-2 rounded w-full text-sm"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Ende <span className="text-red-600">*</span></label>
            <input
              type="date"
              className="border px-3 py-2 rounded w-full text-sm"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              required
            />
          </div>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="px-3 py-2 text-sm border rounded"
          >
            {t('common.cancel')}
          </button>
          <button
            disabled={loading}
            className="px-4 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
          >
            {loading ? t('common.saving') : t('merchant.campaigns.create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCampaignDialog;
